import React from 'react';

export default function AdminTodos({
  token,
  todosData,
  todoText,
  setTodoText,
  todoNotes,
  setTodoNotes,
  todoError,
  todoLoading,
  addTodo,
  fetchTodos,
  toggleTodoDone,
  todoNotesModal,
  setTodoNotesModal,
  saveTodoNotes,
  deleteTodo
}) {
  const rows = Array.isArray(todosData?.rows) ? todosData.rows : [];
  const isDone = (t) => String(t?.done || '').toLowerCase() === 'true';

  const NOTE_MARKER = '[NOTE]';
  const isNoteRow = (t) => String(t?.text || '').trim() === NOTE_MARKER;

  const tasks = rows.filter(t => !isNoteRow(t));
  const notes = rows.filter(t => isNoteRow(t));

  const openTodos = tasks.filter(t => !isDone(t));
  const completedTodos = tasks.filter(t => isDone(t));

  const openNotes = notes.filter(t => !isDone(t));         // usually all notes stay open
  const completedNotes = notes.filter(t => isDone(t));     // if you ever mark them done

  const renderRow = (t, { hideCheckbox = false, titleOverride = null } = {}) => {
    const id = String(t?.id || t?.ID || '').trim();
    const text = t?.text || t?.todo || t?.item || '';
    const notesText = String(t?.note || t?.Note || t?.notes || t?.Notes || '').trim();
    const done = isDone(t);
    const displayTitle = titleOverride ?? (text === NOTE_MARKER ? 'Note' : text);

    return (
      <div
        key={id || displayTitle}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
          padding: '10px 12px',
          borderRadius: 10,
          border: '1px solid #eee',
          background: done ? '#f6f8fa' : '#fff'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          {!hideCheckbox ? (
            <input
              type="checkbox"
              checked={done}
              onChange={() => toggleTodoDone(id, !done)}
              disabled={!id}
              style={{ marginTop: 3 }}
            />
          ) : (
            <div style={{ width: 16 }} />
          )}

          <div>
            <div style={{ textDecoration: done ? 'line-through' : 'none', color: done ? '#666' : '#111' }}>
              {displayTitle}
            </div>

            {notesText ? (
              <div className="muted" style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>
                {notesText}
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          <div style={{ color: '#777', fontSize: 12 }}>
            {t?.createdAt ? new Date(t.createdAt).toLocaleString() : ''}
          </div>

          <button
            className="btn"
            onClick={() => setTodoNotesModal({ open: true, id, text: displayTitle, notes: notesText })}
            disabled={!id}
          >
            Edit note
          </button>

          {String(text).trim() === NOTE_MARKER ? (
            <button
              className="btn"
              onClick={() => {
                if (!id) return;
                const ok = window.confirm("Delete this note? This cannot be undone.");
                if (ok) deleteTodo(id);
              }}
              disabled={!id}
            >
              Delete
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  const addNote = async () => {
    // Reuse the existing addTodo handler, but ensure todoText is empty and todoNotes has the note.
    // This works because backend now allows notes-only and will store text as "[NOTE]".
    if (todoLoading) return;
    const currentText = (todoText || '').trim();
    const currentNotes = (todoNotes || '').trim();
    if (currentText) {
      // If they typed a task, use Add task instead
      return;
    }
    if (!currentNotes) return;

    await addTodo();
  };

  return (
    <div style={{ marginTop: 14 }}>
      <h2 style={{ margin: '6px 0 10px 0' }}>Tasks</h2>

      {/* Add bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Add a task (e.g., Buy paper plates)"
          value={todoText}
          onChange={(e) => setTodoText(e.target.value)}
          style={{ flex: '1 1 280px', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd' }}
        />
        <button className="btn primary" onClick={addTodo} disabled={todoLoading}>
          Add task
        </button>
      </div>

      <div style={{ marginTop: 8 }}>
        <input
          type="text"
          placeholder="Add a note (details, links, reminders, etc.)"
          value={todoNotes}
          onChange={(e) => setTodoNotes(e.target.value)}
          style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd' }}
        />
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn" onClick={addNote} disabled={todoLoading || !!(todoText || '').trim()}>
            Add note
          </button>
        </div>
        <div className="muted" style={{ marginTop: 6, fontSize: 12 }}>
          Leave the task field empty to add a note-only item.
        </div>
      </div>

      {todoError ? <div className="error" style={{ marginTop: 10 }}>{todoError}</div> : null}

      {/* Side-by-side columns */}
      <div
        style={{
          marginTop: 14,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 14
        }}
      >
        {/* Left: Tasks */}
        <div>
          <h3 style={{ margin: '10px 0' }}>Open ({openTodos.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {openTodos.map(t => renderRow(t))}
            {!openTodos.length ? <div className="muted">No open tasks.</div> : null}
          </div>

          <h3 style={{ margin: '16px 0 10px 0' }}>Completed ({completedTodos.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {completedTodos.map(t => renderRow(t))}
            {!completedTodos.length ? <div className="muted">No completed tasks.</div> : null}
          </div>
        </div>

        {/* Right: Notes */}
        <div>
          <h3 style={{ margin: '10px 0' }}>Notes ({openNotes.length})</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {openNotes.map(n => renderRow(n, { hideCheckbox: true, titleOverride: 'Note' }))}
            {!openNotes.length ? <div className="muted">No notes yet.</div> : null}
          </div>

          {completedNotes.length ? (
            <>
              <h3 style={{ margin: '16px 0 10px 0' }}>Archived notes ({completedNotes.length})</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {completedNotes.map(n => renderRow(n, { hideCheckbox: true, titleOverride: 'Note' }))}
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Notes modal (unchanged) */}
      {todoNotesModal?.open ? (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="close" onClick={() => setTodoNotesModal({ open: false, id: '', text: '', notes: '' })}>
              ×
            </button>
            <h3 style={{ marginTop: 0 }}>Edit note</h3>
            <div className="muted" style={{ marginBottom: 8 }}>{todoNotesModal.text}</div>
            <textarea
              value={todoNotesModal.notes}
              onChange={(e) => setTodoNotesModal((p) => ({ ...p, notes: e.target.value }))}
              rows={8}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
              <button className="btn" onClick={() => setTodoNotesModal({ open: false, id: '', text: '', notes: '' })}>
                Cancel
              </button>
              <button className="btn primary" onClick={saveTodoNotes}>
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}